/**
 * V58: 수습사원 평가 탭 컴포넌트
 *
 * OrgMembersPage의 "수습사원평가" 탭에서 렌더링.
 * - 수습 대상자 목록 (probationEndDate >= today)
 * - 차수/상태 표시
 * - 평가서 전달 모달 (멘토 선택 → createProbationEvaluation)
 * - 평가 상세 읽기 전용 패널
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { FileText, CheckCircle, AlertCircle, X } from 'lucide-react';
import { useModalOverlayClose } from '../../../shared/hooks/useModalOverlayClose';
import { Employee } from '../../../shared/types/employee';
import {
  useOrgEvaluations,
  EvaluationItem,
  EvaluationType,
  EVALUATION_TYPE_LABELS,
  EVALUATION_STATUS_LABELS,
  EvaluationStatus,
} from '../../../shared/hooks/useEvaluations';
import { ProbationEvaluationForm, isProbationType, calculateAutoDetailedGrade } from '../../../shared/components/ProbationEvaluationForm';
import type { FinalGrade } from '../../../shared/hooks/useEvaluations';

// ============================================
// Props
// ============================================

interface ProbationEvaluationTabProps {
  employees: Employee[];
  scopeOrgUnitIds: string[];
  onEmployeeNameClick?: (employeeId: string, employeeName: string) => void;
}

// ============================================
// 차수 헬퍼
// ============================================

/**
 * V70.12: 수습평가 알림 정책 적용 기준일 (Cutover).
 *
 * ⚠️ BE 상수와 **반드시 동기화**해야 한다 —
 *    functions/src/modules/notifications/schedulers/reminderSchedule.ts
 *    의 PROBATION_POLICY_CUTOVER_DATE 와 같은 값이어야 한다.
 *    (이 값 하나 때문에 신규 callable/응답 필드를 추가하지 않는다)
 */
const PROBATION_POLICY_CUTOVER_DATE = '2026-09-18';

/**
 * V70.23: 평가서 제출 마감일 오프셋 = 수습만료일 D-7.
 *
 * ⚠️ BE 상수와 **반드시 동기화**해야 한다 —
 *    functions/src/modules/notifications/schedulers/reminderSchedule.ts
 *    의 PROBATION_MANAGER_DEADLINE_OFFSET_DAYS 와 같은 값이어야 한다.
 *    (V70.22 운영 배포 정책. 이 값 하나 때문에 신규 callable/응답 필드를 추가하지 않는다)
 */
const PROBATION_MANAGER_DEADLINE_OFFSET_DAYS = -7;

/**
 * V70.23: YYYY-MM-DD 에 일수 가감.
 *
 * dateUtils 원칙 준수 — `new Date("YYYY-MM-DD")` / `toISOString()` 금지.
 * BE reminderSchedule.ts 의 addDaysYmd 와 동일하게 **수동 파싱 + Date.UTC 기준** 으로 계산한다.
 */
function addDaysYmd(ymd: string, deltaDays: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return ymd;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  d.setUTCDate(d.getUTCDate() + deltaDays);
  const p2 = (n: number) => (n < 10 ? `0${n}` : String(n));
  return `${d.getUTCFullYear()}-${p2(d.getUTCMonth() + 1)}-${p2(d.getUTCDate())}`;
}

/** V70.23: 두 YYYY-MM-DD 사이 일수 (b - a). UTC 기준. */
function daysBetweenYmd(a: string, b: string): number {
  const pa = /^(\d{4})-(\d{2})-(\d{2})$/.exec(a);
  const pb = /^(\d{4})-(\d{2})-(\d{2})$/.exec(b);
  if (!pa || !pb) return 0;
  const ma = Date.UTC(Number(pa[1]), Number(pa[2]) - 1, Number(pa[3]));
  const mb = Date.UTC(Number(pb[1]), Number(pb[2]) - 1, Number(pb[3]));
  return Math.round((mb - ma) / (1000 * 60 * 60 * 24));
}

/** V70.23: 수습만료일 → 평가서 제출 마감일 (수습만료 D-7) */
function getManagerDeadline(probationEndDate: string): string {
  return addDaysYmd(probationEndDate, PROBATION_MANAGER_DEADLINE_OFFSET_DAYS);
}

/**
 * V70.23: 제출 마감일 표시용 계산.
 *
 * @param probationEndDate 수습만료일 (YYYY-MM-DD)
 * @param today            오늘 (YYYY-MM-DD)
 * @param hrSubmitted      최신 cycle probation_3m 이 sent_to_hr | hr_approved 인가
 *                         (= V70.22 조직장 리마인더 중단 조건)
 *
 * 색상 경계 (마감일까지 남은 일수 기준):
 *   ≥15 기본 / 14~8 노랑 / 7~0 주황 / 초과 빨강
 * HR 제출 완료 상태면 경고 문구·색을 모두 제거하고 날짜만 표시한다.
 */
function getDeadlineDisplay(
  probationEndDate: string | undefined,
  today: string,
  hrSubmitted: boolean
): { text: string; style: React.CSSProperties } | null {
  if (!probationEndDate) return null;
  const deadline = getManagerDeadline(probationEndDate);
  if (hrSubmitted) return { text: deadline, style: {} };

  const remaining = daysBetweenYmd(today, deadline); // 양수 = 남음, 0 = 당일, 음수 = 초과
  const suffix =
    remaining > 0 ? `(D-${remaining})` : remaining === 0 ? '(오늘 마감)' : `(${-remaining}일 초과)`;

  let style: React.CSSProperties = {};
  if (remaining <= -1) {
    style = { color: 'var(--color-red-600)', background: 'var(--color-red-50)' };
  } else if (remaining <= 7) {
    style = { color: 'var(--color-orange-600)', background: 'var(--color-orange-50)' };
  } else if (remaining <= 14) {
    style = { color: 'var(--color-yellow-600)', background: 'var(--color-yellow-50)' };
  }
  return { text: `${deadline} ${suffix}`, style };
}

const PROBATION_TYPES: EvaluationType[] = ['probation_1m', 'probation_2m', 'probation_3m'];

function isProbationCompleted(type: EvaluationType, status: EvaluationStatus): boolean {
  if (type === 'probation_1m' || type === 'probation_2m') {
    return ['lead_submitted', 'manager_submitted', 'sent_to_hr', 'hr_approved', 'hr_rejected'].includes(status);
  }
  if (type === 'probation_3m') {
    // V266: hr_rejected 제거 — 3m HR 반려는 조직장 재편집 필요 (완료 아님)
    return ['manager_submitted', 'sent_to_hr', 'hr_approved'].includes(status);
  }
  return false;
}

/** V59→V63.8: HR 제출 가능 상태 판정 — 3차 완료 시에만 1~3차 일괄 제출 */
function canSendToHr(type: EvaluationType, status: EvaluationStatus): boolean {
  // V63.8: probation_3m + manager_submitted 일 때만 HR 제출 가능 (1~3차 일괄)
  if (type === 'probation_3m') {
    return status === 'manager_submitted';
  }
  return false;
}

function getRoundLabel(type: EvaluationType): string {
  switch (type) {
    case 'probation_1m': return '1차';
    case 'probation_2m': return '2차';
    case 'probation_3m': return '3차';
    default: return '-';
  }
}

interface ProbationStatus {
  currentRound: number | null; // 현재 진행 중 차수 (null = 없음)
  currentType: EvaluationType | null;
  currentEval: EvaluationItem | null;
  completedRounds: number[];
  nextRound: number | null; // 다음 생성 가능 차수 (null = 모두 완료)
  allCompleted: boolean;
  /** V59: HR 제출 가능한 평가 */
  hrSendableEval: EvaluationItem | null;
  /** V63.6: probation_3m manager_submitted — 조직장 재편집 가능한 평가 */
  managerEditableEval: EvaluationItem | null;
  /** V266: 3차 평가 — allCompleted 시 상태 표시용 */
  thirdEval: EvaluationItem | null;
  /** V272: 현재 표시 중인 cycle (1=초회, 2+=재평가) */
  latestCycle: number;
}

function computeProbationStatus(evals: EvaluationItem[]): ProbationStatus {
  // V272: 최신 cycle 기준으로만 상태 표시
  const probEvals = evals.filter(e => isProbationType(e.templateType));
  const latestCycle = probEvals.length > 0
    ? Math.max(...probEvals.map(e => e.evaluationCycle || 1))
    : 1;
  const cycleEvals = probEvals.filter(e => (e.evaluationCycle || 1) === latestCycle);

  const byType: Record<string, EvaluationItem[]> = {};
  for (const ev of cycleEvals) {
    if (!byType[ev.templateType]) byType[ev.templateType] = [];
    byType[ev.templateType].push(ev);
  }

  const completedRounds: number[] = [];
  let currentRound: number | null = null;
  let currentType: EvaluationType | null = null;
  let currentEval: EvaluationItem | null = null;
  let hrSendableEval: EvaluationItem | null = null;
  let managerEditableEval: EvaluationItem | null = null;
  let thirdEval: EvaluationItem | null = null; // V266

  for (let i = 0; i < PROBATION_TYPES.length; i++) {
    const type = PROBATION_TYPES[i];
    const typeEvals = byType[type] || [];
    const completed = typeEvals.some(e => isProbationCompleted(type, e.status));
    const inProgress = typeEvals.find(e => !isProbationCompleted(type, e.status));

    // V266: 3차 평가 추적 (allCompleted 시 상태 표시용)
    if (type === 'probation_3m' && typeEvals.length > 0) {
      thirdEval = typeEvals[0];
    }

    if (completed) {
      completedRounds.push(i + 1);
      // V59: 완료된 차수 중 HR 제출 가능한 것 탐색
      const sendable = typeEvals.find(e => canSendToHr(type, e.status));
      if (sendable && !hrSendableEval) hrSendableEval = sendable;
      // V63.6→V63.8: probation_3m manager_submitted 또는 hr_rejected → 조직장 재편집 대상
      if (type === 'probation_3m') {
        const editable = typeEvals.find(e => e.status === 'manager_submitted' || e.status === 'hr_rejected');
        if (editable) managerEditableEval = editable;
      }
    } else if (inProgress) {
      currentRound = i + 1;
      currentType = type;
      currentEval = inProgress;
      // V59: 진행 중 평가도 HR 제출 가능 여부 체크
      if (canSendToHr(type, inProgress.status)) {
        hrSendableEval = inProgress;
      }
    }
  }

  // 다음 차수: 완료되지 않고 진행 중도 아닌 첫 번째 차수
  let nextRound: number | null = null;
  if (!currentRound) {
    for (let i = 0; i < PROBATION_TYPES.length; i++) {
      if (!completedRounds.includes(i + 1)) {
        nextRound = i + 1;
        break;
      }
    }
  }

  return {
    currentRound,
    currentType,
    currentEval,
    completedRounds,
    nextRound,
    allCompleted: completedRounds.length === 3,
    hrSendableEval,
    managerEditableEval,
    thirdEval,
    latestCycle,
  };
}

// V266: 상태별 배지 스타일
function getStatusBadgeStyle(status: EvaluationStatus): { background: string; color: string } {
  switch (status) {
    case 'hr_approved':
      return { background: '#D1FAE5', color: '#065F46' };
    case 'hr_rejected':
    case 'rejected':
      return { background: '#FEE2E2', color: '#DC2626' };
    case 'sent_to_hr':
      return { background: '#EDE9FE', color: '#6D28D9' };
    case 'manager_submitted':
    case 'second_submitted':
      return { background: '#DBEAFE', color: '#1D4ED8' };
    case 'sent_to_upper':
      return { background: '#FEF3C7', color: '#92400E' };
    default:
      return { background: '#FEF3C7', color: '#92400E' };
  }
}

// ============================================
// 메인 컴포넌트
// ============================================

export function ProbationEvaluationTab({ employees, scopeOrgUnitIds, onEmployeeNameClick }: ProbationEvaluationTabProps) {
  const {
    evaluations,
    loading,
    error,
    listOrgEvaluations,
    createProbationEvaluation,
    sendProbationToHr,
    getEvaluationDetail,
    submitManagerEvaluation,
  } = useOrgEvaluations();

  // V62: 수습 대상자 필터 (OrgInterviewsPage 탭 라우팅과 동일 기준)
  // SSoT: probationEndDate >= today → 수습기간 내 → 무조건 포함
  // 예외: probationEndDate < today && 미완료 평가 존재 → 수습 종료 후 관리
  const today = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);

  // 평가 목록 조회 (수습 유형만)
  // V62: 연도 필터 제거 — OrgInterviewsPage와 동일 기준 (연도 경계 누락 방지)
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (scopeOrgUnitIds.length > 0 && !loaded) {
      listOrgEvaluations({});
      setLoaded(true);
    }
  }, [scopeOrgUnitIds, loaded, listOrgEvaluations]);

  // 수습 평가만 필터
  const probationEvals = useMemo(() => {
    return evaluations.filter(e => isProbationType(e.templateType));
  }, [evaluations]);

  /**
   * V70.12: 최신 evaluationCycle 의 probation_3m 이 HR 제출 완료 상태인 직원 집합.
   *  - 판정 기준은 조직장 리마인드 알림(BE probationEvalNotifier)과 **동일한 의미**여야 한다.
   *  - sent_to_hr / hr_approved 만 완료. hr_rejected 는 **미완료**(조직장이 다시 제출해야 함).
   *  - 이미 받아온 평가 목록만 사용한다 (추가 조회 없음).
   */
  const hrSubmittedEmployeeIds = useMemo(() => {
    const latestByEmp: Record<string, { cycle: number; status: string }> = {};
    probationEvals
      .filter(e => e.templateType === 'probation_3m')
      .forEach(e => {
        const cycle = (e as any).evaluationCycle || 1;
        const prev = latestByEmp[e.targetUserId];
        if (!prev || cycle > prev.cycle) {
          latestByEmp[e.targetUserId] = { cycle, status: e.status };
        }
      });
    return new Set(
      Object.entries(latestByEmp)
        .filter(([, v]) => v.status === 'sent_to_hr' || v.status === 'hr_approved')
        .map(([id]) => id)
    );
  }, [probationEvals]);

  // V62: 수습 대상자 판정 — OrgInterviewsPage 탭 라우팅과 동일
  // (1) 수습기간 내 → 무조건 포함 (allCompleted 무관)
  // (2) 수습 종료 && 미완료 평가 존재 → 포함 (수습 후 관리)
  // (3) V70.12: 수습 종료 && 평가문서 0건이어도 **CUTOVER 이후 정책 대상 + HR 미제출**이면 잔류
  const probationEmployees = useMemo(() => {
    const evalEmployeeIds = new Set(probationEvals.map(e => e.targetUserId));

    return employees
      .filter(emp => {
        if (emp.status !== 'active') return false;
        const isInProbationPeriod = !!emp.probationEndDate && emp.probationEndDate >= today;
        if (isInProbationPeriod) return true; // 수습기간 내 → 무조건 포함
        // 수습 종료 후: 평가 이력이 있으면 포함 (관리 필요)
        if (evalEmployeeIds.has(emp.id)) return true;
        // V70.12: 평가문서가 아예 없어도 신규 정책 대상이면 잔류 —
        //   조직장이 리마인드를 받고도 화면에서 대상을 찾지 못하는 문제 해결.
        //   CUTOVER 보호로 과거 레거시 직원은 재등장하지 않는다.
        if (
          !!emp.probationEndDate &&
          emp.probationEndDate >= PROBATION_POLICY_CUTOVER_DATE &&
          !hrSubmittedEmployeeIds.has(emp.id)
        ) {
          return true;
        }
        return false;
      })
      .sort((a, b) => {
        const dateA = a.probationEndDate || '';
        const dateB = b.probationEndDate || '';
        if (dateA !== dateB) return dateA.localeCompare(dateB);
        return (a.nameKo || '').localeCompare(b.nameKo || '', 'ko');
      });
  }, [employees, today, probationEvals, hrSubmittedEmployeeIds]);

  // 대상자별 평가 상태 매핑
  const statusByEmployee = useMemo(() => {
    const map: Record<string, ProbationStatus> = {};
    for (const emp of probationEmployees) {
      const empEvals = probationEvals.filter(e => e.targetUserId === emp.id);
      map[emp.id] = computeProbationStatus(empEvals);
    }
    return map;
  }, [probationEmployees, probationEvals]);

  // 모달 상태
  const [createModal, setCreateModal] = useState<{
    isOpen: boolean;
    employee: Employee | null;
    evaluationCycle?: number;
    nextRound?: number;
  }>({ isOpen: false, employee: null });
  const [selectedMentorId, setSelectedMentorId] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // 상세 패널 상태
  const [detailEval, setDetailEval] = useState<EvaluationItem | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // V63: manager 모드 편집 상태
  const [managerFormData, setManagerFormData] = useState<Record<string, any>>({});
  const [managerSubmitting, setManagerSubmitting] = useState(false);
  const [managerSubmitError, setManagerSubmitError] = useState<string | null>(null);

  // 평가서 전달 모달 열기
  const handleOpenCreateModal = useCallback((employee: Employee, evaluationCycle?: number, nextRound?: number) => {
    setCreateModal({ isOpen: true, employee, evaluationCycle, nextRound });
    setSelectedMentorId('');
    setCreateError(null);
  }, []);

  // 평가서 전달 실행
  const handleCreate = useCallback(async () => {
    if (!createModal.employee || !selectedMentorId) return;
    setCreating(true);
    setCreateError(null);
    try {
      const result = await createProbationEvaluation({
        targetUserId: createModal.employee.id,
        leadEvaluatorId: selectedMentorId,
        ...(createModal.evaluationCycle && createModal.evaluationCycle > 1
          ? { evaluationCycle: createModal.evaluationCycle }
          : {}),
      });
      if (result) {
        alert(`${result.round}차 수습 평가가 생성되었습니다.`);
        setCreateModal({ isOpen: false, employee: null });
        // 목록 새로고침
        listOrgEvaluations({ year: new Date().getFullYear() });
      }
    } catch (err: any) {
      setCreateError(err?.message || '평가 생성 실패');
    } finally {
      setCreating(false);
    }
  }, [createModal.employee, createModal.evaluationCycle, selectedMentorId, createProbationEvaluation, listOrgEvaluations]);

  // V59: HR 제출
  const [sendingHr, setSendingHr] = useState(false);
  const handleSendToHr = useCallback(async (evalItem: EvaluationItem) => {
    if (!window.confirm(`${evalItem.targetUserName}의 ${EVALUATION_TYPE_LABELS[evalItem.templateType]}을 인사팀으로 제출하시겠습니까?`)) return;
    setSendingHr(true);
    try {
      const success = await sendProbationToHr(evalItem.id);
      if (success) {
        alert('인사팀으로 제출되었습니다.');
        listOrgEvaluations({ year: new Date().getFullYear() });
      }
    } finally {
      setSendingHr(false);
    }
  }, [sendProbationToHr, listOrgEvaluations]);

  // V63: 팀장 최종평가 제출 (ProbationEvaluationTab에서)
  const handleManagerSubmit = useCallback(async () => {
    if (!detailEval) return;
    setManagerSubmitting(true);
    setManagerSubmitError(null);
    try {
      // probation_3m: _i16에서 finalGrade/finalGradeDetail 파생
      let submitGrade: FinalGrade | undefined;
      let submitGradeDetail: string | undefined;
      if (detailEval.templateType === 'probation_3m') {
        const i16Key = Object.keys(managerFormData).find(k => k.endsWith('_i16'));
        const i16Value = i16Key ? managerFormData[i16Key] : null;
        if (i16Value && typeof i16Value === 'string') {
          submitGradeDetail = i16Value;
          const gradeChar = i16Value.charAt(0).toUpperCase();
          if (['A', 'B', 'C', 'D'].includes(gradeChar)) {
            submitGrade = gradeChar as FinalGrade;
          }
        }
      }

      const success = await submitManagerEvaluation({
        evaluationId: detailEval.id,
        managerEvaluation: managerFormData,
        finalGrade: submitGrade,
        finalGradeDetail: submitGradeDetail,
      });
      if (success) {
        alert('최종평가가 제출되었습니다.');
        // 목록 새로고침 + 상세 닫기
        listOrgEvaluations({ year: new Date().getFullYear() });
        setDetailEval(null);
      }
    } catch (err: any) {
      setManagerSubmitError(err?.message || '최종평가 제출 실패');
    } finally {
      setManagerSubmitting(false);
    }
  }, [detailEval, managerFormData, submitManagerEvaluation, listOrgEvaluations]);

  // V273: 모달 오버레이 드래그 오작동 방지
  const createModalOverlayProps = useModalOverlayClose(() => setCreateModal({ isOpen: false, employee: null }));
  const detailOverlayProps = useModalOverlayClose(() => setDetailEval(null));
  const reEvalOverlayProps = useModalOverlayClose(() => setReEvalModal({ isOpen: false, evaluation: null }));

  // V63: 재평가 시작
  const [reEvalModal, setReEvalModal] = useState<{ isOpen: boolean; evaluation: EvaluationItem | null }>({ isOpen: false, evaluation: null });
  const [reEvalMentorId, setReEvalMentorId] = useState('');
  const [reEvalCreating, setReEvalCreating] = useState(false);

  const handleStartReEvaluation = useCallback(async () => {
    if (!reEvalModal.evaluation || !reEvalMentorId) return;
    setReEvalCreating(true);
    try {
      const currentCycle = reEvalModal.evaluation.evaluationCycle || 1;
      const result = await createProbationEvaluation({
        targetUserId: reEvalModal.evaluation.targetUserId,
        leadEvaluatorId: reEvalMentorId,
        evaluationCycle: currentCycle + 1,
        parentEvaluationId: reEvalModal.evaluation.id,
      });
      if (result) {
        alert(`재평가 ${currentCycle}차 — ${result.round}차 수습 평가가 생성되었습니다.`);
        setReEvalModal({ isOpen: false, evaluation: null });
        listOrgEvaluations({ year: new Date().getFullYear() });
      }
    } catch (err: any) {
      alert(err?.message || '재평가 생성 실패');
    } finally {
      setReEvalCreating(false);
    }
  }, [reEvalModal.evaluation, reEvalMentorId, createProbationEvaluation, listOrgEvaluations]);

  // 상세 보기
  const handleViewDetail = useCallback(async (evalItem: EvaluationItem) => {
    setDetailLoading(true);
    setManagerSubmitError(null);
    const detail = await getEvaluationDetail(evalItem.id);
    setDetailEval(detail);
    // V63: manager formData 초기화 (기존 managerEvaluation 복원)
    if (detail) {
      const initForm: Record<string, any> = { ...(detail.managerEvaluation || {}) };
      // probation_3m + 자동등급: _i16 초기값 계산
      if (detail.templateType === 'probation_3m' && detail.templateItems) {
        const i16Key = (detail.templateItems || []).find(i => i.id.endsWith('_i16'))?.id;
        if (i16Key && !initForm[i16Key]) {
          const autoGrade = calculateAutoDetailedGrade(detail.leadEvaluation || undefined, detail.templateItems);
          if (autoGrade) initForm[i16Key] = autoGrade;
        }
      }
      setManagerFormData(initForm);
    }
    setDetailLoading(false);
  }, [getEvaluationDetail]);

  // 멘토 후보 목록 (대상자 본인 제외)
  const mentorCandidates = useMemo(() => {
    if (!createModal.employee) return [];
    return employees.filter(emp =>
      emp.id !== createModal.employee!.id && emp.status === 'active'
    );
  }, [employees, createModal.employee]);

  return (
    <div>
      {/* 에러 */}
      {error && (
        <div style={{
          padding: 12, background: '#FEF2F2', borderRadius: 6,
          color: '#DC2626', fontSize: 13, marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* 수습 대상자 테이블 */}
      <div style={{
        background: 'white', borderRadius: 8,
        border: '1px solid var(--color-gray-200)', overflow: 'hidden',
      }}>
        <div style={{
          padding: '12px 16px', borderBottom: '1px solid var(--color-gray-200)',
          background: 'var(--color-gray-50)', fontSize: 13, color: 'var(--color-gray-600)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <FileText size={16} />
          수습 대상자{' '}
          <span style={{ fontWeight: 600, color: 'var(--color-gray-800)' }}>
            {probationEmployees.length}
          </span>
          명
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--color-gray-50)' }}>
                {['이름', '부서', '직책', '입사일', '수습만료일', '평가서 제출 마감일', '진행 차수', '상태', '액션'].map(h => (
                  <th key={h} style={{
                    padding: '12px 16px', textAlign: 'center', fontWeight: 600,
                    color: 'var(--color-gray-700)', borderBottom: '1px solid var(--color-gray-200)',
                    whiteSpace: 'nowrap',
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={9} style={{ padding: 40, textAlign: 'center', color: 'var(--color-gray-500)' }}>
                    평가 목록 조회 중...
                  </td>
                </tr>
              )}
              {!loading && probationEmployees.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ padding: 40, textAlign: 'center', color: 'var(--color-gray-500)' }}>
                    수습 대상자가 없습니다.
                  </td>
                </tr>
              )}
              {!loading && probationEmployees.map(emp => {
                const ps = statusByEmployee[emp.id];
                return (
                  <tr key={emp.id} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                    <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 500 }}>
                      {onEmployeeNameClick ? (
                        <span
                          onClick={(e) => { e.stopPropagation(); onEmployeeNameClick(emp.id, emp.nameKo || '-'); }}
                          style={{ cursor: 'pointer', color: 'inherit', textDecoration: 'none' }}
                        >
                          {emp.nameKo || '-'}
                        </span>
                      ) : (emp.nameKo || '-')}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {emp.orgUnitName || '-'}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {emp.jobTitle || '-'}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {emp.firstHireDate || '-'}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {emp.probationEndDate || '-'}
                    </td>
                    {/* V70.23: 평가서 제출 마감일 (수습만료 D-7). HR 제출 완료 시 경고 제거 */}
                    <td style={{ padding: '12px 16px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {(() => {
                        const dd = getDeadlineDisplay(
                          emp.probationEndDate,
                          today,
                          hrSubmittedEmployeeIds.has(emp.id)
                        );
                        if (!dd) return '-';
                        const hasColor = !!dd.style.color;
                        return (
                          <span style={{
                            ...(hasColor
                              ? { padding: '2px 8px', borderRadius: 12, fontWeight: 500 }
                              : {}),
                            ...dd.style,
                          }}>
                            {dd.text}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {ps?.currentRound
                        ? `${(ps.latestCycle || 1) > 1 ? `재평가${ps.currentRound}` : `${ps.currentRound}`}차`
                        : ps?.allCompleted
                        ? '전체 완료'
                        : ps?.nextRound
                        ? `${(ps.latestCycle || 1) > 1 ? `재평가${ps.nextRound}` : `${ps.nextRound}`}차 대기`
                        : '-'}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {ps?.currentEval ? (() => {
                        const badgeStyle = getStatusBadgeStyle(ps.currentEval!.status);
                        return (
                          <span style={{
                            padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 500,
                            ...badgeStyle,
                          }}>
                            {EVALUATION_STATUS_LABELS[ps.currentEval!.status] || ps.currentEval!.status}
                          </span>
                        );
                      })() : ps?.allCompleted ? (() => {
                        // V266: 3차 평가의 실제 상태 표시 (hardcoded "완료" 제거)
                        const displayStatus = ps.thirdEval?.status || 'hr_approved';
                        const badgeStyle = getStatusBadgeStyle(displayStatus);
                        return (
                          <span style={{
                            padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 500,
                            ...badgeStyle,
                          }}>
                            {EVALUATION_STATUS_LABELS[displayStatus] || displayStatus}
                          </span>
                        );
                      })() : (
                        <span style={{ color: 'var(--color-gray-400)', fontSize: 12 }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {ps?.currentRound && ps.currentEval ? (
                          // V63.4: probation_3m + lead_submitted → "조직장 평가 진행" 버튼
                          ps.currentEval.templateType === 'probation_3m' && ps.currentEval.status === 'lead_submitted' ? (
                            <button
                              onClick={() => handleViewDetail(ps.currentEval!)}
                              style={{
                                padding: '4px 12px', borderRadius: 4, fontSize: 12, fontWeight: 500,
                                border: 'none', background: '#3B82F6',
                                color: 'white', cursor: 'pointer',
                              }}
                            >
                              조직장 평가 진행
                            </button>
                          ) : (
                            <button
                              onClick={() => handleViewDetail(ps.currentEval!)}
                              style={{
                                padding: '4px 12px', borderRadius: 4, fontSize: 12, fontWeight: 500,
                                border: '1px solid var(--color-gray-300)', background: 'white',
                                color: 'var(--color-gray-700)', cursor: 'pointer',
                              }}
                            >
                              진행 중
                            </button>
                          )
                        ) : ps?.allCompleted ? (
                          ps.thirdEval && ['sent_to_hr', 'hr_approved'].includes(ps.thirdEval.status) ? (
                            <button
                              onClick={() => handleViewDetail(ps.thirdEval!)}
                              style={{
                                padding: '4px 12px', borderRadius: 4, fontSize: 12, fontWeight: 500,
                                border: '1px solid var(--color-gray-300)', background: 'white',
                                color: 'var(--color-gray-700)', cursor: 'pointer',
                              }}
                            >
                              상세
                            </button>
                          ) : (
                            <CheckCircle size={16} style={{ color: '#10B981' }} />
                          )
                        ) : ps?.nextRound ? (
                          <button
                            onClick={() => handleOpenCreateModal(emp, ps.latestCycle, ps.nextRound ?? undefined)}
                            style={{
                              padding: '4px 12px', borderRadius: 4, fontSize: 12, fontWeight: 500,
                              border: 'none', background: 'var(--color-primary)',
                              color: 'white', cursor: 'pointer',
                            }}
                          >
                            {(ps.latestCycle || 1) > 1 ? `재평가${ps.nextRound}` : `${ps.nextRound}`}차 평가 전달
                          </button>
                        ) : null}
                        {/* V63.6: probation_3m manager_submitted → "조직장 평가 수정" 버튼 */}
                        {ps?.managerEditableEval && (
                          <button
                            onClick={() => handleViewDetail(ps.managerEditableEval!)}
                            style={{
                              padding: '4px 12px', borderRadius: 4, fontSize: 12, fontWeight: 500,
                              border: 'none', background: '#3B82F6',
                              color: 'white', cursor: 'pointer',
                            }}
                          >
                            조직장 평가 수정
                          </button>
                        )}
                        {/* V59: 인사팀 제출 버튼 */}
                        {ps?.hrSendableEval && (
                          <button
                            onClick={() => handleSendToHr(ps.hrSendableEval!)}
                            disabled={sendingHr}
                            style={{
                              padding: '4px 12px', borderRadius: 4, fontSize: 12, fontWeight: 500,
                              border: 'none', background: sendingHr ? 'var(--color-gray-300)' : '#7C3AED',
                              color: 'white', cursor: sendingHr ? 'not-allowed' : 'pointer',
                            }}
                          >
                            인사팀 제출
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* 평가서 전달 모달 */}
      {createModal.isOpen && createModal.employee && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex',
            justifyContent: 'center', alignItems: 'center', zIndex: 1000,
          }}
          {...createModalOverlayProps}
        >
          <div
            style={{
              background: 'white', borderRadius: 12, padding: 24,
              minWidth: 360, maxWidth: 440, boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>수습사원 평가서 전달</h3>
              <button
                onClick={() => setCreateModal({ isOpen: false, employee: null })}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--color-gray-400)' }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: 'var(--color-gray-600)', marginBottom: 8 }}>
                <strong>대상자:</strong> {createModal.employee.nameKo} ({createModal.employee.orgUnitName || '-'})
              </div>
              <div style={{ fontSize: 13, color: 'var(--color-gray-600)', marginBottom: 12 }}>
                <strong>차수:</strong> {createModal.nextRound ? `${createModal.nextRound}차` : '1차'}{(createModal.evaluationCycle || 1) > 1 ? ` (재평가 ${createModal.evaluationCycle}사이클)` : ''}
              </div>

              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
                멘토 선택
              </label>
              <select
                value={selectedMentorId}
                onChange={e => setSelectedMentorId(e.target.value)}
                style={{
                  width: '100%', padding: 8, border: '1px solid var(--color-gray-300)',
                  borderRadius: 6, fontSize: 13,
                }}
              >
                <option value="">멘토를 선택하세요</option>
                {mentorCandidates.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.nameKo || m.email} ({m.orgUnitName || '-'})
                  </option>
                ))}
              </select>
            </div>

            {createError && (
              <div style={{
                padding: 8, background: '#FEF2F2', borderRadius: 4,
                color: '#DC2626', fontSize: 12, marginBottom: 12,
              }}>
                {createError}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setCreateModal({ isOpen: false, employee: null })}
                style={{
                  padding: '8px 20px', borderRadius: 6, border: '1px solid var(--color-gray-300)',
                  background: 'white', color: 'var(--color-gray-600)', fontSize: 14, fontWeight: 500, cursor: 'pointer',
                }}
              >
                취소
              </button>
              <button
                onClick={handleCreate}
                disabled={!selectedMentorId || creating}
                style={{
                  padding: '8px 20px', borderRadius: 6, border: 'none',
                  background: !selectedMentorId || creating ? 'var(--color-gray-300)' : 'var(--color-primary)',
                  color: 'white', fontSize: 14, fontWeight: 500,
                  cursor: !selectedMentorId || creating ? 'not-allowed' : 'pointer',
                }}
              >
                {creating ? '전달 중...' : '전달'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 평가 상세 패널 — V63: probation_3m manager 모드 지원 */}
      {detailEval && (() => {
        // V63: probation_3m + 허용 상태 → manager 모드
        // V265: hr_rejected도 probation_3m 재편집 허용 (BE submitManagerEvaluation:1463과 일치)
        const isP3mManagerMode = detailEval.templateType === 'probation_3m'
          && ['lead_submitted', 'manager_submitted', 'hr_rejected'].includes(detailEval.status);
        const isHrLocked = detailEval.templateType === 'probation_3m'
          ? ['sent_to_hr', 'hr_approved'].includes(detailEval.status)
          : ['sent_to_hr', 'hr_approved', 'hr_rejected'].includes(detailEval.status);
        const formMode = isP3mManagerMode ? 'manager' as const : 'readonly' as const;
        // V63→V272: 재평가 가능 여부 — HR 승인 이후만 (BE가 자동 생성하므로 FE에서는 안내만)
        const i15Key = (detailEval.templateItems || []).find(i => i.id.endsWith('_i15'))?.id;
        const finalDecision = i15Key
          ? (detailEval.managerEvaluation || {})[i15Key]
          : null;
        const canStartReEval = detailEval.templateType === 'probation_3m'
          && finalDecision === '수습연장(재평가)'
          && detailEval.status === 'hr_approved';

        return (
          <div
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.5)', display: 'flex',
              justifyContent: 'center', alignItems: 'center', zIndex: 1000,
            }}
            {...detailOverlayProps}
          >
            <div
              style={{
                background: 'white', borderRadius: 12, padding: 24,
                width: '90%', maxWidth: 700, maxHeight: '85vh', overflowY: 'auto',
                boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
              }}
              onClick={e => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>
                  {detailEval.targetUserName} — {EVALUATION_TYPE_LABELS[detailEval.templateType] || detailEval.templateType}
                  {(detailEval.evaluationCycle || 1) > 1 && (
                    <span style={{ fontSize: 13, color: '#7C3AED', marginLeft: 8 }}>
                      (재평가 {(detailEval.evaluationCycle || 1) - 1}차)
                    </span>
                  )}
                </h3>
                <button
                  onClick={() => setDetailEval(null)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--color-gray-400)' }}
                >
                  <X size={20} />
                </button>
              </div>

              <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--color-gray-600)' }}>
                상태: <strong>{EVALUATION_STATUS_LABELS[detailEval.status] || detailEval.status}</strong>
                {detailEval.leadEvaluatorName && (
                  <> | 멘토: <strong>{detailEval.leadEvaluatorName}</strong></>
                )}
                {isP3mManagerMode && (
                  <span style={{ marginLeft: 8, padding: '2px 8px', background: '#DBEAFE', color: '#1D4ED8', borderRadius: 12, fontSize: 11, fontWeight: 500 }}>
                    편집 가능
                  </span>
                )}
                {isHrLocked && (
                  <span style={{ marginLeft: 8, padding: '2px 8px', background: '#FEE2E2', color: '#DC2626', borderRadius: 12, fontSize: 11, fontWeight: 500 }}>
                    수정 불가
                  </span>
                )}
              </div>

              {detailLoading ? (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-gray-500)' }}>
                  상세 조회 중...
                </div>
              ) : detailEval.templateNodes && detailEval.templateItems && detailEval.templateScoringScale ? (
                <>
                  <ProbationEvaluationForm
                    mode={formMode}
                    nodes={detailEval.templateNodes}
                    items={detailEval.templateItems}
                    scoringScale={detailEval.templateScoringScale}
                    templateType={detailEval.templateType}
                    selfEvaluation={detailEval.selfEvaluation}
                    leadEvaluation={detailEval.leadEvaluation || undefined}
                    managerEvaluation={detailEval.managerEvaluation || undefined}
                    formData={isP3mManagerMode ? managerFormData : {}}
                    onChange={isP3mManagerMode ? ((id, val) => setManagerFormData(prev => ({ ...prev, [id]: val }))) : undefined}
                    attendanceSummary={detailEval.attendanceSummary}
                  />

                  {/* V63: manager 모드 제출 버튼 */}
                  {isP3mManagerMode && (
                    <div style={{ marginTop: 16 }}>
                      {managerSubmitError && (
                        <div style={{ padding: 10, background: '#FEF2F2', color: '#DC2626', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
                          {managerSubmitError}
                        </div>
                      )}
                      <button
                        onClick={handleManagerSubmit}
                        disabled={managerSubmitting}
                        style={{
                          padding: '10px 20px', border: 'none', borderRadius: 6, width: '100%',
                          background: managerSubmitting ? '#9CA3AF' : '#3B82F6', color: 'white',
                          cursor: managerSubmitting ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 14,
                        }}
                      >
                        {managerSubmitting ? '저장 중...' : '최종평가 저장'}
                      </button>
                    </div>
                  )}

                  {/* V272: 재평가 승인 안내 (BE가 자동 생성) */}
                  {canStartReEval && (
                    <div style={{
                      marginTop: 12, padding: '12px 16px', background: '#EDE9FE',
                      border: '1px solid #C4B5FD', borderRadius: 8, fontSize: 13, color: '#5B21B6',
                    }}>
                      인사팀 승인 완료 — 재평가 {detailEval.evaluationCycle || 1}차가 자동 생성되었습니다.
                      목록에서 진행 상태를 확인하세요.
                    </div>
                  )}
                </>
              ) : (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--color-gray-500)', fontSize: 13 }}>
                  평가 데이터를 불러올 수 없습니다.
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* V63: 재평가 시작 모달 */}
      {reEvalModal.isOpen && reEvalModal.evaluation && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex',
            justifyContent: 'center', alignItems: 'center', zIndex: 1100,
          }}
          {...reEvalOverlayProps}
        >
          <div
            style={{
              background: 'white', borderRadius: 12, padding: 24,
              minWidth: 360, maxWidth: 440, boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>재평가 시작</h3>
              <button
                onClick={() => setReEvalModal({ isOpen: false, evaluation: null })}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--color-gray-400)' }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: 'var(--color-gray-600)', marginBottom: 8 }}>
                <strong>대상자:</strong> {reEvalModal.evaluation.targetUserName}
              </div>
              <div style={{ fontSize: 13, color: 'var(--color-gray-600)', marginBottom: 8 }}>
                <strong>현재 사이클:</strong> {reEvalModal.evaluation.evaluationCycle || 1}차
              </div>
              <div style={{ fontSize: 13, color: '#7C3AED', marginBottom: 12 }}>
                → 재평가 {(reEvalModal.evaluation.evaluationCycle || 1)}차 시작 (1차~3차 수습 평가 새로 진행)
              </div>

              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
                멘토 선택
              </label>
              <select
                value={reEvalMentorId}
                onChange={e => setReEvalMentorId(e.target.value)}
                style={{
                  width: '100%', padding: 8, border: '1px solid var(--color-gray-300)',
                  borderRadius: 6, fontSize: 13,
                }}
              >
                <option value="">멘토를 선택하세요</option>
                {employees.filter(m => m.id !== reEvalModal.evaluation!.targetUserId && m.status === 'active').map(m => (
                  <option key={m.id} value={m.id}>
                    {m.nameKo || m.email} ({m.orgUnitName || '-'})
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setReEvalModal({ isOpen: false, evaluation: null })}
                style={{
                  padding: '8px 20px', borderRadius: 6, border: '1px solid var(--color-gray-300)',
                  background: 'white', color: 'var(--color-gray-600)', fontSize: 14, fontWeight: 500, cursor: 'pointer',
                }}
              >
                취소
              </button>
              <button
                onClick={handleStartReEvaluation}
                disabled={!reEvalMentorId || reEvalCreating}
                style={{
                  padding: '8px 20px', borderRadius: 6, border: 'none',
                  background: !reEvalMentorId || reEvalCreating ? 'var(--color-gray-300)' : '#7C3AED',
                  color: 'white', fontSize: 14, fontWeight: 500,
                  cursor: !reEvalMentorId || reEvalCreating ? 'not-allowed' : 'pointer',
                }}
              >
                {reEvalCreating ? '생성 중...' : '재평가 시작'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
